import type { AxiosRequestConfig } from 'axios'

import { handleException } from 'clients/exception-handling'
import { toQueryPanel } from 'features/charts-v2/types/chart-query-panel'
import type {
  DashboardExportPayload,
  DashboardExportResult,
  ShareDashboardResponseType,
  SharedUsersResponse,
} from 'features/dashboard/service/types'
import { DASHBOARDS_2_URL } from 'features/dashboards-v2/paths'
import type {
  DashboardPreviewResponse,
  DashboardResponse,
  NewDashboardPayload,
  UpdateDashboardPayload,
} from 'features/dashboards-v2/types'
import { normalizeDashboardResponse } from 'features/dashboards-v2/utils/normalize-stored-keywords'

const DASHBOARDS_BASE_URL = '/custom-dashboards'
const CLIENT_DASHBOARDS_V2_BASE_URL = '/client/dashboards'
const HUB_UUID_HEADER = 'X-Hub-UUID'

type ClientResponse<T> = {
  data: T
  headers: Record<string, string | undefined>
}

type DashboardV2Client = {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<ClientResponse<T>>
  post<T>(
    url: string,
    body?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ClientResponse<T>>
  patch<T>(
    url: string,
    body?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ClientResponse<T>>
  delete<T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ClientResponse<T>>
}

/**
 * Dashboard V2 service - fetches dashboards with V2 chart translation applied.
 *
 * This service mirrors the read operations from DashboardV2Service but points
 * to the /dashboards BFF endpoints, which apply v1->v2 chart
 * translation to all chart items before returning.
 *
 * Note: Write operations (create, update, delete, share) remain on V1 endpoints
 * and are not duplicated here. Use the original DashboardV2Service for those.
 */
export class DashboardV2Service {
  private client: DashboardV2Client

  constructor(client: unknown) {
    this.client = client as DashboardV2Client
  }

  async getDashboards(proj_uuid: string): Promise<DashboardResponse[]> {
    const { data } = await this.client.get<DashboardResponse[]>(
      `${DASHBOARDS_BASE_URL}`,
      {
        params: {
          proj_uuid: proj_uuid,
        },
      },
    )

    return Array.isArray(data) ? data.map(normalizeDashboardResponse) : data
  }

  async getDashboardPreviews(
    proj_uuid: string,
    includeAll = false,
  ): Promise<DashboardPreviewResponse[]> {
    const { data } = await this.client.get<DashboardPreviewResponse[]>(
      `${CLIENT_DASHBOARDS_V2_BASE_URL}`,
      {
        headers: {
          [HUB_UUID_HEADER]: proj_uuid,
        },
        params: includeAll ? { include_all: true } : undefined,
      },
    )
    return data
  }

  async getDashboard(
    proj_uuid: string,
    id: number | string,
  ): Promise<DashboardResponse> {
    const { data } = await this.client.get<DashboardResponse>(
      `${DASHBOARDS_BASE_URL}/${id}`,
      {
        params: {
          proj_uuid: proj_uuid,
        },
      },
    )

    return normalizeDashboardResponse(data)
  }

  async getDashboardByUuid(
    proj_uuid: string,
    dashboardUuid: string,
  ): Promise<DashboardResponse> {
    const { data } = await this.client.get<DashboardResponse>(
      `${CLIENT_DASHBOARDS_V2_BASE_URL}/${dashboardUuid}`,
      {
        headers: {
          [HUB_UUID_HEADER]: proj_uuid,
        },
      },
    )
    return normalizeDashboardResponse(data)
  }

  async getPublicDashboardByUuid(
    proj_uuid: string,
    dashboardUuid: string,
  ): Promise<DashboardResponse> {
    const { data } = await this.client.get<DashboardResponse>(
      `${CLIENT_DASHBOARDS_V2_BASE_URL}/public/${dashboardUuid}`,
      {
        headers: {
          [HUB_UUID_HEADER]: proj_uuid,
        },
      },
    )
    return normalizeDashboardResponse(data)
  }

  async getPublicDashboardByLegacyId(
    proj_uuid: string,
    dashboardId: string,
  ): Promise<DashboardResponse> {
    const { data } = await this.client.get<DashboardResponse>(
      `${CLIENT_DASHBOARDS_V2_BASE_URL}/public/id/${dashboardId}`,
      {
        headers: {
          [HUB_UUID_HEADER]: proj_uuid,
        },
      },
    )
    return normalizeDashboardResponse(data)
  }

  async getDashboardByLegacyId(
    proj_uuid: string,
    dashboardId: string,
  ): Promise<DashboardResponse> {
    const { data } = await this.client.get<DashboardResponse>(
      `${CLIENT_DASHBOARDS_V2_BASE_URL}/id/${dashboardId}`,
      {
        headers: {
          [HUB_UUID_HEADER]: proj_uuid,
        },
      },
    )
    return normalizeDashboardResponse(data)
  }

  // Fires a charts-v2 aggregate query with an arbitrary panel. Used by the
  // template save path to snapshot top-N breakdown values from a sibling
  // item's ranking (see `panel_filter_from_ranking` in the template engine).
  // With grouped:true on the breakdown, response has parent-rolled-up rows
  // (`parent_data`) + a parent→child map (`category_mapping`, keyed under
  // the wire dimension e.g. "cluster_topic" for theme). Falls back to
  // `data` (flat child rows) when the breakdown isn't groupable.
  async fetchChartRanking(
    proj_uuid: string,
    panel: unknown,
  ): Promise<{
    rows: Array<{ category: string | number; volume: number }>
    childExpansion: Record<string, string[]>
  } | null> {
    try {
      const { data } = await this.client.post<
        | ({
            data?: Array<{
              category: (string | number | null)[]
              values: (string | number | null)[]
            }>
            parent_data?: Array<{
              category: (string | number | null)[]
              values: (string | number | null)[]
            }> | null
            category_mapping?: Record<string, Record<string, string[]>> | null
          } & Record<string, unknown>)
        | null
      >(
        '/charts/chart',
        { panel: toQueryPanel(panel as never) },
        { params: { proj_uuid } },
      )
      const rows = Array.isArray(data?.parent_data)
        ? data.parent_data
        : (data?.data ?? null)
      if (!Array.isArray(rows)) return null
      const flat = rows
        .map((r) => {
          const cat = r.category?.[0]
          const vol = r.values?.[0]
          if (cat == null || typeof vol !== 'number') return null
          return { category: cat, volume: vol }
        })
        .filter(
          (r): r is { category: string | number; volume: number } => r !== null,
        )
      const cm = data?.category_mapping ?? {}
      const childExpansion = Object.values(cm)[0] ?? {}
      return { rows: flat, childExpansion }
    } catch (cause) {
      handleException(cause, { operation: 'dashboards-v2.service.action' })

      return null
    }
  }

  async saveDashboard(
    proj_uuid: string,
    body: NewDashboardPayload,
  ): Promise<DashboardResponse> {
    const { data } = await this.client.post<DashboardResponse>(
      DASHBOARDS_BASE_URL,
      {
        ...body,
      },
      {
        params: {
          proj_uuid: proj_uuid,
        },
      },
    )
    return normalizeDashboardResponse(data)
  }

  async updateDashboard(
    proj_uuid: string,
    body: UpdateDashboardPayload,
  ): Promise<DashboardResponse> {
    const { data } = await this.client.patch<DashboardResponse>(
      `${DASHBOARDS_BASE_URL}/${body.dashboard_id}`,
      {
        ...body,
      },
      {
        params: {
          proj_uuid: proj_uuid,
        },
      },
    )
    return normalizeDashboardResponse(data)
  }

  async exportDashboardToPPTX(
    proj_uuid: string,
    payload: DashboardExportPayload,
    customParams?: Omit<AxiosRequestConfig, 'params' | 'responseType'>,
  ): Promise<DashboardExportResult> {
    const response = await this.client.post<ArrayBuffer>(
      `${DASHBOARDS_BASE_URL}/export`,
      payload,
      {
        params: {
          proj_uuid: proj_uuid,
        },
        responseType: 'arraybuffer',
        ...customParams,
      },
    )
    return {
      data: response.data,
      contentDisposition: response.headers['content-disposition'],
    }
  }

  async deleteDashboard(
    proj_uuid: string,
    dashboard_id: number,
  ): Promise<void> {
    await this.client.delete(`${DASHBOARDS_BASE_URL}/${dashboard_id}`, {
      params: {
        proj_uuid: proj_uuid,
      },
    })
  }

  async shareDashboard(
    proj_uuid: string,
    body: {
      id: number
      as_owners: boolean
      email_addresses: string[]
      message: string
    },
  ): Promise<ShareDashboardResponseType> {
    const { data } = await this.client.post<ShareDashboardResponseType>(
      `${DASHBOARDS_BASE_URL}/${body.id}/share`,
      {
        shared_users_emails: body.email_addresses,
        as_owners: body.as_owners,
        is_shared: true,
        message: body.message,
        url_segment: DASHBOARDS_2_URL,
      },
      {
        params: {
          proj_uuid: proj_uuid,
        },
      },
    )
    return data
  }

  async unshareDashboard(
    proj_uuid: string,
    body: { dashboard_id: number; email_addresses: string[] },
  ): Promise<void> {
    await this.client.post(
      `${DASHBOARDS_BASE_URL}/${body.dashboard_id}/unshare`,
      { shared_users_emails: body.email_addresses, is_shared: false },
      {
        params: {
          proj_uuid: proj_uuid,
        },
      },
    )
  }

  async getPublicUrl(proj_uuid: string, dashboard_id: number): Promise<string> {
    const { data } = await this.client.post<string>(
      `${DASHBOARDS_BASE_URL}/${dashboard_id}/create_public_link`,
      { url_segment: DASHBOARDS_2_URL },
      {
        params: { proj_uuid },
      },
    )
    return data
  }

  async fetchSharedUsers(proj_uuid: string): Promise<SharedUsersResponse> {
    const { data } = await this.client.get<SharedUsersResponse>(
      '/project/shared',
      {
        params: { proj_uuid },
      },
    )
    return data
  }

  async generateDashboard(
    proj_uuid: string,
    category: string,
    value: string,
    theme: string,
  ): Promise<DashboardResponse> {
    const body =
      category === 'brand'
        ? {
            focus: category,
            brand: value,
            theme: theme,
          }
        : {
            product: value,
            analysis: 'pdp',
          }

    const { data } = await this.client.post<DashboardResponse>(
      `${DASHBOARDS_BASE_URL}/automated-analysis`,
      body,
      {
        params: {
          proj_uuid,
        },
      },
    )
    return normalizeDashboardResponse(data)
  }
}
